import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Alert, Badge, Button, Card, DataTable, PageHeader, Spinner } from "@empite/ui-components";
import type { Operator, RoutingProfileSummary } from "../admin-client";
import { useRoutingProfiles } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { FlagBadge, StatusBadge, when } from "./common";
import { RoutingProfileForm } from "./routing-profile-form";

/** The permissions this page's write needs, and the one the markup plan
 *  select needs; repeated here only to decide what to draw. */
const WRITE = "providers:write";
const PRICING_READ = "pricing:read";

/**
 * Routing profiles — the rules that decide which upstream an application's
 * traffic actually reaches.
 *
 * Scope is the column to read first. A profile with no organization is the
 * application's default and applies to everybody under it; one with an
 * organization overrides that default for a single customer, and disabling
 * the wrong one of the two is the difference between an outage for one
 * account and an outage for all of them.
 */
export function RoutingProfiles() {
  return (
    <RequireOperator>{(session) => <ProfileList operator={session.operator} />}</RequireOperator>
  );
}

export function profileScope(profile: RoutingProfileSummary): string {
  return profile.organization_id === undefined ? "application default" : "organization";
}

function ProfileList({ operator }: { operator: Operator }) {
  const profiles = useRoutingProfiles();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const writable = can(operator, WRITE);

  if (profiles.isPending) {
    return <Spinner label="Loading routing profiles" />;
  }
  if (profiles.error) {
    return <ErrorAlert error={profiles.error} title="Could not load the routing profiles" />;
  }

  const rows = profiles.data?.routing_profiles ?? [];

  return (
    <>
      <PageHeader
        title="Routing"
        description="Which providers each application's requests are offered to, in what order, under which markup."
        actions={
          writable ? (
            <Button
              variant="primary"
              onClick={() => {
                setNotice(null);
                setCreating(true);
              }}
            >
              New profile
            </Button>
          ) : null
        }
      />
      {notice ? <Alert tone="success" title={notice} /> : null}
      <Card>
        {/* A created profile is disabled and empty, so the operator is taken
            straight to its page: the provider chain and the model policies
            are the next two things it needs, and neither is on this screen. */}
        {writable && creating ? (
          <RoutingProfileForm
            target={{ mode: "create" }}
            canReadPricing={can(operator, PRICING_READ)}
            onClose={() => setCreating(false)}
            onDone={(profile, message) => {
              setCreating(false);
              setNotice(message);
              void navigate({
                to: "/routing-profiles/$profileId",
                params: { profileId: profile.id },
              });
            }}
          />
        ) : null}
        <DataTable<RoutingProfileSummary>
          rows={rows}
          rowKey={(row) => row.id}
          empty="No routing profile has been configured, so nothing has a route."
          columns={[
            { key: "application", header: "Application", render: (row) => row.application_name },
            {
              key: "profile",
              header: "Profile",
              render: (row) => (
                <>
                  <Link to="/routing-profiles/$profileId" params={{ profileId: row.id }}>
                    {row.name}
                  </Link>
                  <div>
                    <code className="esm-muted-text">{row.slug}</code>
                  </div>
                </>
              ),
            },
            {
              key: "status",
              header: "Status",
              render: (row) => <StatusBadge status={row.status} />,
            },
            {
              key: "scope",
              header: "Scope",
              render: (row) => (
                <>
                  <Badge tone={row.organization_id === undefined ? "neutral" : "warning"}>
                    {profileScope(row)}
                  </Badge>
                  {row.organization_id === undefined ? null : (
                    <div>
                      <code className="esm-muted-text">{row.organization_id}</code>
                    </div>
                  )}
                </>
              ),
            },
            {
              key: "markup",
              header: "Markup plan",
              // No plan means the platform default applies, which is a fact
              // worth stating: an empty cell reads as "no markup".
              render: (row) =>
                row.markup_plan_name ? (
                  row.markup_plan_name
                ) : (
                  <span className="esm-muted-text">platform default</span>
                ),
            },
            {
              key: "flags",
              header: "Behaviour",
              render: (row) => (
                <div className="esm-badge-row">
                  <FlagBadge on={row.fallback_enabled} label="fallback" />
                  <FlagBadge on={row.edge_enabled} label="edge" />
                </div>
              ),
            },
            {
              key: "counts",
              header: "Providers / policies",
              align: "end",
              render: (row) => `${row.provider_count} / ${row.model_policy_count}`,
            },
            { key: "created", header: "Created", render: (row) => when(row.created_at) },
          ]}
        />
      </Card>
    </>
  );
}
